import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, X } from "@phosphor-icons/react";

import { BLANK_CONFIG, TYPE_META } from "@/components/delivery/destinationConstants";
import { buildConfig, blankSecretsForEdit, stripBlankSecretsForPatch } from "@/components/delivery/destinationHelpers";
import DestinationForm from "@/components/delivery/DestinationForm";
import { EMPTY_NOTIFY, notifyParams } from "@/components/NotifyBuyerBox";
import DestinationTable from "@/components/delivery/DestinationTable";
import QuickCloneModal from "@/components/delivery/QuickCloneModal";
import BulkCloneModal from "@/components/delivery/BulkCloneModal";
import InviteBuyerModal from "@/components/delivery/InviteBuyerModal";
import InvoicePreviewModal from "@/components/delivery/InvoicePreviewModal";
import QuickSetupBuyerModal from "@/components/delivery/QuickSetupBuyerModal";
import BuyerCombobox from "@/components/shared/BuyerCombobox";
import HelpLink from "@/components/HelpLink";
import { AdminModal } from "@/components/pageSettings/AdminModal";
import { TestReceipt } from "@/components/testleads/TestReceipt";

// Iter-61.4 defaults; Iter-61.4.1 swaps Buyer preload for async combobox.
const DEFAULT_SORT = "created_desc";
const DEFAULT_STATUS = "all";
const DEFAULT_TYPE = "all";
const PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { value: "created_desc", label: "Newest" },
  { value: "created_asc",  label: "Oldest" },
  { value: "name_asc",     label: "Name A → Z" },
  { value: "name_desc",    label: "Name Z → A" },
];

export default function DeliveryDestinations() {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, page_size: PAGE_SIZE, has_next: false });
  const [showNew, setShowNew] = useState(false);
  const [showQuickSetup, setShowQuickSetup] = useState(false);
  const [testReceipt, setTestReceipt] = useState(null);
  const [quickCloneSource, setQuickCloneSource] = useState(null);
  const [bulkCloneSource, setBulkCloneSource] = useState(null);
  const [inviteBuyerSource, setInviteBuyerSource] = useState(null);
  const [invoicePreviewSource, setInvoicePreviewSource] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [notify, setNotify] = useState(EMPTY_NOTIFY);
  const [editingName, setEditingName] = useState(null);
  const [editingClientName, setEditingClientName] = useState(null);
  const [form, setForm] = useState({
    client_id: "", name: "", type: "webhook",
    config: { ...BLANK_CONFIG },
    campaign_ids: [], is_active: true, is_default: false,
    retry_policy: { max_attempts: 5 },
    // Iter-66: per-destination affiliate blocklist. Persisted via
    // `blocked_affiliate_ids` on POST /delivery/destinations and PATCH.
    blocked_affiliate_ids: [],
    // Iter-74.2: optional buyer economics. Left null means "not set" —
    // Delivery Destinations preserves whatever the existing value is on
    // Edit-load, and Create sends null (so backend doesn't persist a 0).
    buyer_price: null,
    return_window_hours: null,
  });

  // Filters + pagination.
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [typeFilter, setTypeFilter] = useState(DEFAULT_TYPE);
  // Iter-61.4.1 — Buyer filter is now a `{id, name}` object (or null)
  // driven by the async BuyerCombobox, NOT a preloaded array of orgs.
  const [buyerFilter, setBuyerFilter] = useState(null);
  const [page, setPage] = useState(1);

  const filtersActive = q !== "" || sort !== DEFAULT_SORT || status !== DEFAULT_STATUS
    || typeFilter !== DEFAULT_TYPE || !!buyerFilter?.id;

  const loadDestinations = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status !== "all") params.set("status", status);
    if (typeFilter !== "all") params.set("type", typeFilter);
    if (buyerFilter?.id) params.set("client_id", buyerFilter.id);
    params.set("sort", sort);
    params.set("page", String(page));
    params.set("page_size", String(PAGE_SIZE));
    const { data } = await api.get(`/delivery/destinations?${params.toString()}`);
    setRows(data.destinations || []);
    if (data.pagination) setPagination(data.pagination);
  }, [q, status, typeFilter, buyerFilter, sort, page]);

  const loadCampaigns = useCallback(async () => {
    // Iter-61.4.2 — Campaigns are no longer preloaded here. The
    // `<CampaignPicker>` inside `<DestinationForm>` queries
    // `/campaigns?client_id=<buyer>&q=...&page_size=25` on-demand,
    // and any campaign_ids the destination already stores are
    // rehydrated via `POST /campaigns/resolve`. This makes
    // Campaign selection scale beyond the old 500-row cap.
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);
  useEffect(() => { loadDestinations(); }, [loadDestinations]);

  useEffect(() => { setPage(1); }, [q, status, typeFilter, buyerFilter, sort]);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQ(qInput.trim()), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [qInput]);

  const clearFilters = () => {
    setQInput("");
    setQ("");
    setSort(DEFAULT_SORT);
    setStatus(DEFAULT_STATUS);
    setTypeFilter(DEFAULT_TYPE);
    setBuyerFilter(null);
    setPage(1);
  };

  const load = useCallback(async () => {
    await loadDestinations();
  }, [loadDestinations]);

  // Iter-61.4.2 — `filteredCampaigns` was a local prefilter over the
  // preloaded campaign array. CampaignPicker now queries the server
  // directly scoped to `form.client_id`, so this prop is a no-op
  // (accepted for backwards-compat within DestinationForm).
  const filteredCampaigns = [];

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const cleanConfig = stripBlankSecretsForPatch(buildConfig(form));
        const patch = {
          name: form.name,
          config: cleanConfig,
          campaign_ids: form.campaign_ids,
          retry_policy: form.retry_policy,
          is_active: form.is_active,
          is_default: form.is_default,
          // Iter-66 — always send blocked_affiliate_ids so unblocking
          // (clearing the list) also persists.
          blocked_affiliate_ids: form.blocked_affiliate_ids || [],
          buyer_price: form.buyer_price ?? null,
          return_window_hours: form.return_window_hours ?? null,
        };
        await api.patch(`/delivery/destinations/${editingId}`, patch, notifyParams(notify));
        setNotify(EMPTY_NOTIFY);
        toast.success("Destination updated");
        setEditingId(null);
        setEditingName(null);
        setEditingClientName(null);
        setShowNew(false);
        setForm((prev) => ({ ...prev, config: { ...BLANK_CONFIG } }));
        await loadDestinations();
        return;
      }
      const payload = { ...form, config: buildConfig(form) };
      await api.post("/delivery/destinations", payload);
      toast.success("Destination created");
      setShowNew(false);
      setForm({ ...form, config: { ...BLANK_CONFIG } });
      await loadDestinations();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "state_overlap") {
        const conflictList = (detail.overlaps || [])
          .map((o) => `${o.name} [${o.overlapping_states.join(", ")}]`)
          .join("; ");
        if (confirm(
          `${detail.overlaps.length} destination(s) already cover one or more of these states:\n\n${conflictList}\n\nProceed anyway?`,
        )) {
          try {
            const payload = { ...form, config: buildConfig(form) };
            await api.post("/delivery/destinations?force=true", payload);
            toast.success("Destination created (overlap forced)");
            setShowNew(false);
            setForm({ ...form, config: { ...BLANK_CONFIG } });
            await loadDestinations();
          } catch (err2) {
            toast.error(err2.response?.data?.detail || "Failed");
          }
        }
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed"));
      }
    }
  };

  const clone = (r) => {
    const cfg = { ...BLANK_CONFIG, ...(r.config || {}) };
    if (Array.isArray(cfg.state_allowlist)) {
      cfg.state_allowlist = cfg.state_allowlist.join(", ");
    }
    delete cfg.daily_digest_last_sent_at;
    setForm({
      client_id: r.client_id || "",
      name: `${r.name || "destination"} (copy)`,
      type: r.type,
      config: cfg,
      campaign_ids: r.campaign_ids || [],
      is_active: false,
      is_default: false,
      retry_policy: { max_attempts: r.retry_policy?.max_attempts || 5 },
      // Iter-66: carry the blocklist through the clone so a cloned
      // destination doesn't silently start accepting a blocked affiliate.
      blocked_affiliate_ids: r.blocked_affiliate_ids || [],
    });
    // Not in edit mode → keep client name empty so combobox refetches on select.
    setEditingClientName(r.client_name || null);
    setShowNew(true);
    toast.success("Cloned — review and save");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const test = async (id) => {
    try {
      const { data } = await api.post(`/delivery/destinations/${id}/test`);
      if (data.ok) toast.success(`OK (${data.code}) · sample lead with ${data.fields_sent} fields sent`);
      else toast.error(`Failed (${data.code}): ${data.response_body?.slice(0, 80)}`);
      setTestReceipt({ ...data, status: data.ok ? "delivered" : "failed", response_code: data.code });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this destination?")) return;
    await api.delete(`/delivery/destinations/${id}`);
    toast.success("Deleted");
    load();
  };

  const toggle = async (r) => {
    await api.patch(`/delivery/destinations/${r.id}`, { is_active: !r.is_active });
    load();
  };

  const sendDigestNow = async (r) => {
    try {
      const { data } = await api.post(`/delivery/destinations/${r.id}/digest-send-now`);
      if (data.status === "no_attempts") {
        toast.info("No leads delivered yet in the last 24 hours — nothing to send");
      } else if (data.status === "no_recipient") {
        toast.error("This destination has no email recipient configured");
      } else if (data.status === "sent") {
        toast.success(`Digest sent to ${data.recipient} (${data.n_attempts} leads)`);
      } else if (data.status === "failed") {
        toast.error("Digest send failed — check email proxy logs");
      } else {
        toast.success(`Digest run: ${data.status}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const quickClone = (r) => setQuickCloneSource(r);
  const bulkClone = (r) => setBulkCloneSource(r);
  const inviteBuyer = (r) => setInviteBuyerSource(r);
  const invoicePreview = (r) => setInvoicePreviewSource(r);

  const edit = (r) => {
    const cfg = { ...BLANK_CONFIG, ...(r.config || {}) };
    if (Array.isArray(cfg.state_allowlist)) {
      cfg.state_allowlist = cfg.state_allowlist.join(", ");
    }
    if (cfg.state_fees && typeof cfg.state_fees === "object") {
      cfg.state_fees_text = Object.entries(cfg.state_fees)
        .map(([k, v]) => `${k}:${v}`).join(", ");
    }
    delete cfg.daily_digest_last_sent_at;
    const safeCfg = blankSecretsForEdit(cfg);
    setForm({
      client_id: r.client_id || "",
      name: r.name || "",
      type: r.type,
      config: safeCfg,
      campaign_ids: r.campaign_ids || [],
      is_active: !!r.is_active,
      is_default: !!r.is_default,
      retry_policy: { max_attempts: r.retry_policy?.max_attempts || 5 },
      // Iter-66: hydrate the blocklist so the picker renders the
      // currently-persisted affiliates as chips on Edit-open.
      blocked_affiliate_ids: r.blocked_affiliate_ids || [],
      buyer_price: r.buyer_price ?? null,
      return_window_hours: r.return_window_hours ?? null,
    });
    setEditingId(r.id);
    setEditingName(r.name);
    // Iter-61.4.1 — display the buyer name that the backend already
    // enriched onto this row (safe join, name-only). If it wasn't
    // included for any reason, BuyerCombobox will resolve via
    // /organizations/{id}.
    setEditingClientName(r.client_name || null);
    setNotify(EMPTY_NOTIFY);
    setShowNew(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setNotify(EMPTY_NOTIFY);
    setEditingId(null);
    setEditingName(null);
    setEditingClientName(null);
    setShowNew(false);
    setForm((prev) => ({ ...prev, config: { ...BLANK_CONFIG } }));
  };

  const total = pagination.total || 0;
  const visible = rows.length;
  const start = total === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
  const end   = (pagination.page - 1) * pagination.page_size + visible;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="label-mono">Outbound</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">Delivery destinations</h1>
          <div className="mt-1 text-sm text-zinc-600">
            Email, Webhook, Lawmatics, Law Ruler, Lead Docket, Salesforce, Litify, HubSpot, Zoho, Pipedrive, S3, and FTP. Failed attempts retry with exponential backoff (30s → 6h, 5 tries).
          </div>
        </div>
        <div className="flex items-center gap-2 self-start md:self-auto">
          <button
            onClick={() => setShowQuickSetup(true)}
            data-testid="quick-setup-open-btn"
            className="border-2 border-black bg-emerald-700 text-white px-3 py-2 text-xs uppercase tracking-widest font-bold hover:bg-emerald-800 inline-flex items-center gap-1"
            title="Fastest way to onboard a new email buyer (no CRM / webhook config needed)"
          >
            <Plus size={12} className="inline" /> Quick setup (email)
          </button>
          <button onClick={() => {
              if (showNew && editingId) { cancelEdit(); return; }
              setShowNew((v) => !v);
            }} data-testid="new-dest-btn"
            className="bg-black text-white border border-black px-3 py-2 text-xs uppercase tracking-widest hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100">
            <Plus size={12} className="inline mr-1" /> {showNew ? "Close" : "New destination"}
          </button>
          <HelpLink slug="admin-delivery-destinations" label="Delivery guide" testId="help-link-delivery" />
        </div>
      </div>

      <div className="brut-card p-3 mb-3 flex flex-col md:flex-row md:items-center gap-2 md:flex-wrap"
           data-testid="dest-toolbar">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search name, buyer, type, or ID"
            className="w-full border border-black pl-7 pr-2 py-1.5 text-sm"
            data-testid="dest-search-input"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-56">
            <BuyerCombobox
              value={buyerFilter}
              onChange={setBuyerFilter}
              includeAll
              allLabel="Buyer: All"
              placeholder="Buyer: All"
              testIdPrefix="dest-buyer-filter"
            />
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="dest-type-filter"
            className="border border-black px-2 py-1.5 text-sm">
            <option value="all">Type: All</option>
            {Object.entries(TYPE_META).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="dest-status-filter"
            className="border border-black px-2 py-1.5 text-sm">
            <option value="all">Status: All</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)}
            data-testid="dest-sort-select"
            className="border border-black px-2 py-1.5 text-sm">
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {filtersActive && (
            <button onClick={clearFilters} data-testid="dest-clear-filters"
              className="border border-black px-2 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1">
              <X size={12} /> Clear filters
            </button>
          )}
        </div>
      </div>

      {showNew && (
        <DestinationForm
          key={editingId || "new"}
          form={form}
          setForm={setForm}
          filteredCampaigns={filteredCampaigns}
          onSubmit={submit}
          mode={editingId ? "edit" : "create"}
          editingId={editingId}
          editingName={editingName}
          editingClientName={editingClientName}
          onCancel={cancelEdit}
          notify={notify}
          setNotify={setNotify}
          notifyPreview={() => {
            const raw = form.config?.state_allowlist;
            const states = Array.isArray(raw) ? raw : String(raw || "").split(/[\s,]+/).filter(Boolean);
            return { kind: "states", dest_id: editingId, states };
          }}
        />
      )}

      <DestinationTable
        rows={rows}
        onToggle={toggle}
        onTest={test}
        onDelete={del}
        onEdit={edit}
        onClone={clone}
        onQuickClone={quickClone}
        onBulkClone={bulkClone}
        onSendDigest={sendDigestNow}
        onInviteBuyer={inviteBuyer}
        onInvoicePreview={invoicePreview}
      />

      <div className="mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs font-mono uppercase tracking-widest text-zinc-700">
        <div data-testid="dest-result-count">
          {total === 0
            ? "No destinations match"
            : `Showing ${start}${start !== end ? `–${end}` : ""} of ${total} destinations`}
        </div>
        <div className="flex items-center gap-2" data-testid="dest-pagination">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
            data-testid="dest-page-prev"
            className="border border-black px-2 py-1 disabled:opacity-30 hover:bg-black hover:text-white">Prev</button>
          <span>Page {pagination.page || 1}</span>
          <button onClick={() => setPage(page + 1)} disabled={!pagination.has_next}
            data-testid="dest-page-next"
            className="border border-black px-2 py-1 disabled:opacity-30 hover:bg-black hover:text-white">Next</button>
        </div>
      </div>

      {quickCloneSource && (
        <QuickCloneModal
          source={quickCloneSource}
          onClose={() => setQuickCloneSource(null)}
          onSaved={load}
        />
      )}
      {bulkCloneSource && (
        <BulkCloneModal
          source={bulkCloneSource}
          onClose={() => setBulkCloneSource(null)}
          onSaved={load}
        />
      )}
      {inviteBuyerSource && (
        <InviteBuyerModal
          destination={inviteBuyerSource}
          onClose={() => setInviteBuyerSource(null)}
        />
      )}
      {invoicePreviewSource && (
        <InvoicePreviewModal
          destination={invoicePreviewSource}
          onClose={() => setInvoicePreviewSource(null)}
        />
      )}
      {showQuickSetup && (
        <QuickSetupBuyerModal
          onClose={() => setShowQuickSetup(false)}
          onCreated={() => load()}
        />
      )}
      {testReceipt && (
        <AdminModal eyebrow="Test lead receipt" title={`What ${testReceipt.destination_name} got`} onClose={() => setTestReceipt(null)} testId="dest-test-receipt-modal" wide>
          <p className="mb-2 text-[11px] text-zinc-600">
            {testReceipt.ok ? "Delivered." : `Not delivered (${testReceipt.code}).`} This is a made-up TEST lead (Jane Sample) with every field this buyer gets.
          </p>
          <TestReceipt attempt={testReceipt} testId="dest-test-receipt" />
        </AdminModal>
      )}
    </div>
  );
}
